/**
 * @fileoverview Icon Registry Context — Decentralized Icon Management
 *
 * Provides an `IconRegistryProvider` and `useIconRegistry` hook that allow
 * each pod/repo to supply its own icon mappings while keeping the framework
 * free of static icon bundle overhead.
 *
 * Only `useIconRegistry()` — consumed by `FAIcon` (a client component) —
 * calls `useContext`.
 *
 * ## How it works
 *
 * 1. The framework creates a React context with an **empty default value**.
 *    Without a provider, `resolveIcon` returns `undefined` for all lookups.
 *
 * 2. The app wraps its tree with `<IconRegistryProvider registry={appIconRegistry}>`
 *    where `appIconRegistry` is the auto-generated merged registry containing
 *    framework defaults + pod icons.
 *
 * 3. `FAIcon` calls `useIconRegistry()` to resolve icons from the nearest provider.
 */

import { createContext, useContext } from 'react';
import { IconStyle } from '../Icons/types/enums';
import type { IconRegistryMap, ResolveIcon, IconRegistryProviderProps } from './types/propsType';

/**
 * Deep-merges one or more icon registry maps (left-to-right; later entries win).
 *
 * @example
 * // Two-arg (backward-compatible):
 * mergeRegistries(base, override)
 *
 * // Variadic (replaces reduce pattern):
 * mergeRegistries(registryA, registryB, registryC, registryD)
 */
export function mergeRegistries(...registries: IconRegistryMap[]): IconRegistryMap {
  const merged: IconRegistryMap = {};

  for (const registryMap of registries) {
    if (!registryMap || typeof registryMap !== 'object') {
      continue;
    }

    for (const style of Object.keys(registryMap) as `${IconStyle}`[]) {
      merged[style] = { ...(merged[style] || {}), ...registryMap[style] };
    }
  }

  return merged;
}
const IconRegistryContext = createContext<ResolveIcon>(() => undefined);

IconRegistryContext.displayName = 'IconRegistryContext';

/**
 * Provides an icon registry to all `<FAIcon>` components in its subtree.
 */
export function IconRegistryProvider({ children, registry }: IconRegistryProviderProps) {
  const resolveIcon: ResolveIcon = (iconStyle, iconName) => (registry ?? {})[iconStyle]?.[iconName];

  return <IconRegistryContext.Provider value={resolveIcon}>{children}</IconRegistryContext.Provider>;
}

IconRegistryProvider.displayName = 'IconRegistryProvider';

/**
 * Returns a stable `resolveIcon` function from the nearest `IconRegistryProvider`.
 * Returns `() => undefined` when no provider is present in the tree.
 */
export function useIconRegistry(): ResolveIcon {
  return useContext(IconRegistryContext);
}

/**
 * Helper to create a properly-typed `IconRegistryMap`.
 */
export function createIconRegistry(map: IconRegistryMap): IconRegistryMap {
  return map;
}

export type { IconRegistryMap, ResolveIcon, IconRegistryProviderProps };
